import { toast } from "react-hot-toast";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { showMutationError } from "utils/showMutationError";

const RESOURCE = "patientvaccinerefusal";

// This resource answers in the `ApiResponse` envelope, unlike the Healthlink endpoints on
// the same screen which return a bare DTO. The shared axios instance already unwraps
// `{status: "success", data}`, so reading `response.data.data` here would unwrap it twice
// and render an empty screen with no error to explain it.
const getTenant = () => localStorage.getItem("kinesin-tenant");

const refusalKeys = {
  reasons: (tenant) => ["vaccineRefusalReasons", tenant],
  forPatient: (tenant, patientId, standingOnly) => [
    "vaccineRefusals",
    tenant,
    patientId,
    standingOnly,
  ],
  report: (tenant, from, to) => ["vaccineRefusalReport", tenant, from, to],
};

const invalidateRefusals = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ["vaccineRefusals"] });
  queryClient.invalidateQueries({ queryKey: ["vaccineRefusalReport"] });
};

/**
 * The reason picker's options (KIN-1841). Always rendered from here rather than
 * hardcoded: the list is provisional and awaiting clinical sign-off, and each reason
 * carries the `outcome` that says whether choosing it means the patient declined or the
 * dose was clinically withheld.
 */
export const useVaccineRefusalReasons = () => {
  const tenantVal = getTenant();

  return useQuery({
    queryKey: refusalKeys.reasons(tenantVal),
    enabled: !!tenantVal,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${tenantVal}/${RESOURCE}/reasons`);

      return response.data;
    },
    meta: { errorMessage: "Error fetching refusal reasons" },
  });
};

export const usePatientVaccineRefusals = (patientId, standingOnly = false) => {
  const tenantVal = getTenant();

  return useQuery({
    queryKey: refusalKeys.forPatient(tenantVal, patientId, standingOnly),
    enabled: !!tenantVal && !!patientId,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/${RESOURCE}/patient/${patientId}`,
        { params: { standingOnly } }
      );

      return response.data;
    },
    meta: { errorMessage: "Error fetching declined vaccines" },
  });
};

/** The practice-level follow-up list: who declined what, over a date range. */
export const useVaccineRefusalReport = ({ from, to, enabled = true }) => {
  const tenantVal = getTenant();

  return useQuery({
    queryKey: refusalKeys.report(tenantVal, from, to),
    enabled: enabled && !!tenantVal && !!from && !!to,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${tenantVal}/${RESOURCE}/report`, {
        params: { from, to },
      });

      return response.data;
    },
    meta: { errorMessage: "Error fetching the declined-vaccines report" },
  });
};

export const useRecordVaccineRefusal = () => {
  const queryClient = useQueryClient();
  const tenantVal = getTenant();

  return useMutation({
    meta: { skipGlobalError: true },
    onError: (error) => showMutationError(error),
    // `refusalReasonDescription` is deliberately never sent: the backend fills it from the
    // code, so the stored wording cannot drift from the reason it belongs to.
    mutationFn: async (refusal) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(`${tenantVal}/${RESOURCE}`, refusal);

      return response.data;
    },
    onSuccess: () => {
      toast.success("Declined vaccine recorded.");
      invalidateRefusals(queryClient);
    },
  });
};

export const useAmendVaccineRefusal = () => {
  const queryClient = useQueryClient();
  const tenantVal = getTenant();

  return useMutation({
    meta: { skipGlobalError: true },
    onError: (error) => showMutationError(error),
    mutationFn: async (refusal) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(`${tenantVal}/${RESOURCE}`, refusal);

      return response.data;
    },
    onSuccess: () => {
      toast.success("Declined vaccine updated.");
      invalidateRefusals(queryClient);
    },
  });
};

/**
 * The parent reconsidered. Deliberately not a delete: the vaccine having once been
 * declined stays in the record, and only its effect on what is outstanding stops. Under
 * the Phase 2 RSV rule this is a routine path, not an edge case — a parent who declines
 * in the maternity setting can opt in later at the GP.
 */
export const useWithdrawVaccineRefusal = () => {
  const queryClient = useQueryClient();
  const tenantVal = getTenant();

  return useMutation({
    meta: { skipGlobalError: true },
    onError: (error) => showMutationError(error),
    mutationFn: async (id) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(
        `${tenantVal}/${RESOURCE}/${id}/withdraw`
      );

      return response.data;
    },
    onSuccess: () => {
      toast.success("Withdrawn — the vaccine is outstanding again.");
      invalidateRefusals(queryClient);
    },
  });
};

/** Recorded in error. Not the route for a change of mind — that is a withdrawal. */
export const useDeleteVaccineRefusal = () => {
  const queryClient = useQueryClient();
  const tenantVal = getTenant();

  return useMutation({
    meta: { skipGlobalError: true },
    onError: (error) => showMutationError(error),
    mutationFn: async (id) => {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(`${tenantVal}/${RESOURCE}/${id}`);
    },
    onSuccess: () => {
      toast.success("Record removed.");
      invalidateRefusals(queryClient);
    },
  });
};
